import Delta from 'quill-delta'

export const getLastHistoryVersion = history =>
  history.sortBy(historyItem => historyItem.get('to')).last()

export const getVersionBefore = (numVersion, history) => {
  const sortedHistory = history.sortBy(historyItem => -historyItem.get('to'))
  const indexVersion = sortedHistory.findIndex(
    version => version.get('to') === numVersion
  )
  return sortedHistory.get(indexVersion + 1)
}

export const getDeltaBetween = (
  { oVersionNum, nVersionNum, history },
  { withInfo = false } = {}
) =>
  history
    .filter(historyItem => historyItem.get('to') <= nVersionNum)
    .filter(historyItem => !oVersionNum || historyItem.get('to') > oVersionNum)
    .sortBy(historyItem => historyItem.get('to'))
    .map(historyItem => historyItem.toJS())
    .reduce((memo, historyItem) => {
      let ops = historyItem.op.ops || historyItem.op
      if (withInfo) {
        ops = ops.map(op => ({
          ...op,
          attributes: {
            ...op.attributes,
            history: historyItem,
          },
        }))
      }
      return memo.compose(new Delta(ops))
    }, new Delta([]))

export const getSnapshotAt = (history, version) =>
  history
    .filter(historyItem => historyItem.get('to') <= version)
    .sortBy(historyItem => historyItem.get('to'))
    .map(historyItem => historyItem.toJS())
    .reduce(
      (memo, historyItem) => memo.compose(new Delta(historyItem.op)),
      new Delta([])
    )

const addAttributes = (op, attributes) => {
  const oAttributes = op.attributes
  const nAttributes = { ...oAttributes, ...attributes }
  op.attributes = nAttributes
  return op
}

const concatOps = (delta, ops) => {
  const deltaOp = new Delta(ops)
  return delta.concat(deltaOp)
}

const concatOp = (delta, op) => concatOps(delta, [op])

export const createDecoratedDiff = (oDelta, nDelta, shortenDiffDelta) => {
  let oOffset = 0
  let nOffset = 0
  const diffDelta =
    nDelta.length() !== shortenDiffDelta.length()
      ? shortenDiffDelta.retain(nDelta.length() - shortenDiffDelta.length())
      : shortenDiffDelta
  return diffDelta.reduce((memo, op) => {
    if (typeof op.retain === 'number') {
      if (op.attributes && Object.keys(op.attributes).length > 0) {
        // Insert text added with attribute added
        // Insert text removed with attribute removed
        const oInsertDelta = oDelta.slice(oOffset, oOffset + op.retain)
        const nInsertDelta = nDelta.slice(nOffset, nOffset + op.retain)
        const decoratedRemovedOps = oInsertDelta.ops.map(op =>
          addAttributes(op, { removed: true })
        )
        const decoratedAddedOps = nInsertDelta.ops.map(op =>
          addAttributes(op, { added: true })
        )
        const deltaWithRemovedOps = concatOps(memo, decoratedRemovedOps)
        const deltaWithAddedOps = concatOps(
          deltaWithRemovedOps,
          decoratedAddedOps
        )
        oOffset += op.retain
        nOffset += op.retain
        return deltaWithAddedOps
      }
      // OR Insert retained text
      const insertDelta = oDelta.slice(oOffset, oOffset + op.retain)
      oOffset += op.retain
      nOffset += op.retain
      return memo.concat(insertDelta)
    }
    if (typeof op.insert === 'string') {
      // Insert text added with attribute added
      nOffset += op.insert.length
      const decoratedOp = addAttributes(op, { added: true })
      return concatOp(memo, decoratedOp)
    }
    if (typeof op['delete'] === 'number') {
      // Insert text removed with attribute removed
      const removedDelta = oDelta.slice(oOffset, oOffset + op['delete'])
      oOffset += op['delete']
      const decoratedRemovedOps = removedDelta.ops.map(op =>
        addAttributes(op, { removed: true })
      )
      return concatOps(memo, decoratedRemovedOps)
    }
    return memo
  }, new Delta())
}

export const createDecoratedSnapshotBetweenVersions = ({
  oVersionNum,
  nVersionNum,
  history,
}) => {
  const oDelta = oVersionNum ? getSnapshotAt(history, oVersionNum) : new Delta()
  const nDelta = getSnapshotAt(history, nVersionNum)

  const diffDelta = getDeltaBetween({ oVersionNum, nVersionNum, history })
  return createDecoratedDiff(oDelta, nDelta, diffDelta)
}

const getOffsetOfFirstChange = ops => {
  let startOffset = false
  ops.reduce((memoOffset, op) => {
    if (!startOffset === false) {
      return memoOffset
    }
    if (op.attributes && (op.attributes.added || op.attributes.removed)) {
      startOffset = memoOffset
      return memoOffset
    }
    return memoOffset + new Delta([op]).length()
  }, 0)
  return startOffset
}

export const computeHistoryEndingSnapshots = snapshot => {
  const ops = snapshot.ops
  const startOffset = getOffsetOfFirstChange(ops)
  if (startOffset === false) {
    return null
  }

  const startSnapshot = snapshot
    .slice(startOffset, startOffset + 150)
    .insert('\n')

  // slice will preserve the orginal array (~clone)
  ops.slice().reverse()
  const endOffset = snapshot.length() - getOffsetOfFirstChange(ops)

  const endSnapshot =
    endOffset > startOffset + 150 &&
    snapshot
      .slice(endOffset - 150 < 0 ? 0 : endOffset - 150, endOffset)
      .insert('\n')

  return { startSnapshot, endSnapshot }
}

/**
 * @description Given a Delta change diff, it returns the modified intervals as position
 * @param {Object} delta Delta representing the changes
 * @return {object[]>} An array of changed intervals e.g. [[0,1], [56,64]]
 */
export const getDeltaModifiedRanges = delta => {
  let index = 0
  return delta.reduce((ranges, op) => {
    if (op.insert) {
      ranges = ranges.concat([
        [
          index,
          index + op.insert.length,
          (op.attributes && op.attributes.history) || {},
        ],
      ])
      index += op.insert.length
    }
    if (op.retain) {
      index += op.retain
    }
    return ranges
  }, [])
}
